import React, { useState } from "react";
import { NavLink, useNavigate, useLocation, Outlet } from "react-router-dom";
import {
  LayoutDashboard, Users, PackagePlus, ClipboardList, Warehouse,
  Boxes, ReceiptText, LogOut, Menu, Bell, Search, HeartPulse, MessageCircle, UserCog, HeartHandshake, Stethoscope, Settings, UserCircle, Activity,
  Wallet, Truck, FileBarChart2, Archive, ChevronDown, Plug, Tag, Send,
} from "lucide-react";
import { permDe } from "../utils/permissoes";
import { useBoxToggles } from "../hooks/useBoxToggles";
import { useApp } from "../context/AppContext";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { Button } from "./ui/button";
import { FloatingChat } from "./chat/FloatingChat";

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, mod: "dashboard", roles: ["administrador"] },
  { to: "/analitico", label: "Painel Analítico", icon: Activity, mod: "analitico", roles: ["administrador"] },
  { to: "/painel", label: "Painel", icon: HeartHandshake, mod: "painel", roles: ["cuidador", "tecnico_enfermagem", "enfermeiro"] },
  { to: "/residentes", label: "Residentes", icon: Users, mod: "residentes", roles: ["administrador", "cuidador", "tecnico_enfermagem", "enfermeiro"] },
  { to: "/fornecimento", label: "Fornecimento Familiar", icon: PackagePlus, mod: "fornecimento", roles: ["administrador", "cuidador", "tecnico_enfermagem", "enfermeiro"] },
  { to: "/consumo", label: "Consumo de Internação", icon: ClipboardList, mod: "consumo", roles: ["administrador", "cuidador", "tecnico_enfermagem", "enfermeiro"] },
  { to: "/estoque", label: "Estoque da Empresa", icon: Warehouse, mod: "estoque", roles: ["administrador"] },
  {
    group: "Produtos", icon: Boxes, mod: "produtos", roles: ["administrador", "enfermeiro"],
    children: [
      { to: "/produtos", label: "Produtos", icon: Boxes },
      { to: "/inventario", label: "Inventário", icon: Archive, mod: "inventario", roles: ["administrador"] },
    ],
  },
  {
    group: "Financeiro", icon: Wallet, mod: "financeiro", roles: ["administrador"],
    children: [
      { to: "/financeiro/fornecedores", label: "Fornecedores", icon: Truck, mod: "fornecedores", roles: ["administrador"] },
      { to: "/financeiro/contas-pagar", label: "Contas a Pagar", icon: Wallet, mod: "contas_pagar", roles: ["administrador"] },
      { to: "/faturas", label: "Faturas (Contas a Receber)", icon: ReceiptText, mod: "faturas", roles: ["administrador"] },
      { to: "/financeiro/servicos-extras", label: "Serviços Extras", icon: Tag, mod: "servicos_extras", roles: ["administrador"] },
      { to: "/financeiro/relatorios", label: "Relatórios / DRE", icon: FileBarChart2, mod: "dre", roles: ["administrador"] },
    ],
  },
  { to: "/mensagens", label: "Mensagens", icon: MessageCircle, mod: "mensagens", roles: ["administrador", "enfermeiro"] },
  { to: "/notificacoes", label: "Notificações", icon: MessageCircle, mod: "notificacoes", roles: ["administrador"] },
  { to: "/painel-envios", label: "Painel de Envios", icon: Send, mod: "envios", roles: ["administrador"] },
  { to: "/usuarios", label: "Usuários", icon: UserCog, mod: "usuarios", roles: ["administrador"] },
  {
    group: "Configurações", icon: Settings, mod: "configuracoes", roles: ["administrador"],
    children: [
      { to: "/configuracoes", label: "Configurações", icon: Settings },
      { to: "/integracoes", label: "Integrações", icon: Plug, mod: "integracoes", roles: ["administrador"] },
    ],
  },
  { to: "/meu-perfil", label: "Meu Perfil", icon: UserCircle, roles: ["administrador", "enfermeiro", "medico", "nutricionista", "fisioterapeuta", "tecnico_enfermagem", "cuidador", "gerente"] },
];

const perfilLabel = {
  administrador: "Administrador",
  cuidador: "Cuidador(a)",
  tecnico_enfermagem: "Téc./Aux. Enfermagem",
  enfermeiro: "Enfermeiro(a)",
};

const linkCls = ({ isActive }) =>
  `flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
    isActive ? "bg-blue-50 text-blue-700" : "text-slate-600 hover:bg-slate-100"
  }`;

const NavGroup = ({ item, onNavigate }) => {
  const location = useLocation();
  const childActive = item.children.some((c) => location.pathname.startsWith(c.to));
  const [open, setOpen] = useState(childActive);
  return (
    <div>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid={`nav-group-${item.group.toLowerCase()}`}
        className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-medium transition-colors ${childActive ? "text-blue-700" : "text-slate-600 hover:bg-slate-100"}`}
      >
        <item.icon className="w-[18px] h-[18px]" />
        <span className="flex-1 text-left">{item.group}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="ml-4 mt-1 space-y-1 border-l border-slate-100 pl-2">
          {item.children.map((c) => (
            <NavLink key={c.to} to={c.to} onClick={onNavigate} className={linkCls} data-testid={`nav-${c.to.replace(/\//g, "-")}`}>
              <c.icon className="w-[16px] h-[16px]" />
              {c.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
};

const Layout = () => {
  const { auth, logout, acessoFinanceiro, naoLidasChat, openChat } = useApp();
  useBoxToggles();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const perfil = auth?.perfil || "administrador";

  const custom = auth?.permissoes && typeof auth.permissoes === "object" && Object.keys(auth.permissoes).length > 0;
  const verMod = (mod) => !!permDe(auth?.permissoes, mod)?.ver;
  const items = navItems.map((i) => {
    if (!i.children) return i;
    const podeFilho = (c) => !c.roles || perfil === "administrador" || (custom ? verMod(c.mod) : (i.group === "Financeiro" ? acessoFinanceiro : c.roles.includes(perfil)));
    return { ...i, children: i.children.filter(podeFilho) };
  }).filter((i) => {
    if (i.children && i.children.length === 0) return false;
    if (perfil === "administrador") return i.group === "Financeiro" ? true : i.roles.includes(perfil);
    if (custom) {
      if (i.children) return true; // grupo visível se algum filho passou
      if (!i.mod) return i.roles.includes(perfil); // Meu Perfil e afins
      return verMod(i.mod);
    }
    if (i.group === "Financeiro") return acessoFinanceiro;
    return i.roles.includes(perfil);
  });

  const iniciais = (auth?.nome || "AD").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-slate-200 transform transition-transform duration-300 lg:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center gap-2.5 h-16 px-5 border-b border-slate-100">
          <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-blue-600 text-white">
            <HeartPulse className="w-5 h-5" />
          </div>
          <div className="leading-tight">
            <p className="text-sm font-bold text-slate-800">São Jorge</p>
            <p className="text-[11px] text-slate-400">Casa de Repouso</p>
          </div>
        </div>

        <nav className="p-3 space-y-1 overflow-y-auto max-h-[calc(100vh-8rem)]">
          {items.map((item) =>
            item.group ? (
              <NavGroup key={item.group} item={item} onNavigate={() => setOpen(false)} />
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                className={linkCls}
                data-testid={`nav-${item.to.replace(/\//g, "-")}`}
              >
                <item.icon className="w-[18px] h-[18px]" />
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-slate-100">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-3.5 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-red-50 hover:text-red-600 transition-colors"
          >
            <LogOut className="w-[18px] h-[18px]" />
            Sair
          </button>
        </div>
      </aside>

      {/* Overlay mobile */}
      {open && (
        <div
          className="fixed inset-0 z-30 bg-black/30 lg:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Main */}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 flex items-center justify-between h-16 px-4 sm:px-6 bg-white/80 backdrop-blur border-b border-slate-200">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </Button>
            <div className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 w-72">
              <Search className="w-4 h-4 text-slate-400" />
              <input
                placeholder="Buscar..."
                className="bg-transparent text-sm outline-none w-full text-slate-600"
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button onClick={() => openChat()} className={`relative p-2 rounded-lg hover:bg-slate-100 transition-colors ${naoLidasChat?.total > 0 ? "animate-pulse" : ""}`} data-testid="sino-mensagens" title="Mensagens e avisos">
              <Bell className={`w-5 h-5 ${naoLidasChat?.total > 0 ? "text-amber-500" : "text-slate-500"}`} />
              {naoLidasChat?.total > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full" data-testid="sino-badge">{naoLidasChat.total}</span>
              )}
            </button>
            <div className="flex items-center gap-2.5">
              <Avatar className="w-9 h-9">
                <AvatarFallback className="bg-blue-600 text-white text-xs">{iniciais}</AvatarFallback>
              </Avatar>
              <div className="hidden sm:block leading-tight">
                <p className="text-sm font-semibold text-slate-800">{auth?.nome}</p>
                <p className="text-[11px] text-slate-400">{perfilLabel[perfil] || auth?.cargo}</p>
              </div>
            </div>
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
      <FloatingChat />
    </div>
  );
};

export default Layout;
